import type { ReactNode } from "react"
import { useState, useEffect, useMemo } from "react"
import { format, startOfYear, endOfYear, eachMonthOfInterval } from "date-fns"
import { getDateFnsLocale } from "@/v2-common/utils/utils.date"
import Button from "@/v2-ui/button"
import Calendar from "@/v2-ui/calendar"
import Popover from "@/v2-ui/popover"
import cn from "@/v2-ui/utils/utils.cn"
import Select from "@/v2-ui/select"
import { usePrimitiveText } from "@/v2-ui/text/offline"

export type DatePickerProps = {
  renderTrigger: ReactNode
  date: Date | undefined
  setDate: (date?: Date) => void // @todo: should be prefixed with `on` for consistency
  disabled?: boolean
  /** Earliest pickable date — earlier days are disabled in the calendar, and a
   *  month/year jump below the bound clamps to it (DateRangePicker passes the
   *  range's other bound so an inverted range cannot be picked). */
  minDate?: Date
  /** Latest pickable date — see minDate. */
  maxDate?: Date
  /** Show a popover footer with a Reset button that clears the date. */
  showReset?: boolean
  id?: string
  /**
   * Where the calendar popover and its month/year dropdowns portal to
   * (default `#mount`). A picker inside a surface that itself portals to
   * `body` passes `"body"`, or the calendar opens under that surface.
   */
  portalContainer?: string
  /** Extra classes on the calendar popover, e.g. a `z-50` beside `portalContainer="body"`. */
  popoverContentClassName?: string
}

const FIRST_YEAR = 1900
const FUTURE_YEARS = 10

// persisted filter values can arrive as ISO strings after a JSON round-trip
function coerceDate(value?: Date | string): Date | undefined {
  if (!value) return undefined
  if (value instanceof Date) return value
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? undefined : date
}

// Keep a committed date inside [minDate, maxDate] — the calendar disables
// out-of-bounds days, but the month/year selects can still jump past a bound.
function clampToBounds(
  date: Date | undefined,
  minDate?: Date,
  maxDate?: Date,
): Date | undefined {
  if (!date) return date
  if (minDate && date < minDate) return minDate
  if (maxDate && date > maxDate) return maxDate
  return date
}

export function DatePicker(props: DatePickerProps) {
  const { getText } = usePrimitiveText()
  const {
    renderTrigger,
    date: rawDate,
    setDate,
    disabled,
    minDate,
    maxDate,
    showReset,
    portalContainer,
    popoverContentClassName,
    id,
  } = props
  const date = coerceDate(rawDate)
  const [open, setOpen] = useState(false)

  const commitDate = (next?: Date) => {
    setDate(clampToBounds(next, minDate, maxDate))
  }

  const [month, setMonth] = useState<number>(
    date ? date.getMonth() : new Date().getMonth(),
  )
  const [year, setYear] = useState<number>(
    date ? date.getFullYear() : new Date().getFullYear(),
  )

  // Newest first, 1900 up to at least ten years ahead. The viewed year is
  // always in the list: the calendar arrows can step past the window, and a
  // year without an option leaves the select blank.
  const years = useMemo(() => {
    const lastYear = Math.max(
      new Date().getFullYear() + FUTURE_YEARS,
      year,
      maxDate?.getFullYear() ?? 0,
    )
    return Array.from(
      { length: lastYear - FIRST_YEAR + 1 },
      (_, i) => lastYear - i,
    )
  }, [year, maxDate])

  const months = useMemo(() => {
    if (year) {
      return eachMonthOfInterval({
        start: startOfYear(new Date(year, 0, 1)),
        end: endOfYear(new Date(year, 0, 1)),
      })
    }
    return []
  }, [year])

  useEffect(() => {
    if (date) {
      setMonth(date.getMonth())
      setYear(date.getFullYear())
    }
  }, [date])

  const handleYearChange = (selectedYear: string) => {
    const newYear = Number.parseInt(selectedYear, 10)
    setYear(newYear)
    if (date) {
      const newDate = new Date(date)
      newDate.setFullYear(newYear)
      commitDate(newDate)
    }
  }

  const handleMonthChange = (selectedMonth: string) => {
    const newMonth = Number.parseInt(selectedMonth, 10)
    setMonth(newMonth)
    if (date) {
      const newDate = new Date(date)
      newDate.setMonth(newMonth)
      commitDate(newDate)
    } else {
      commitDate(new Date(year, newMonth, 1))
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild id={id}>
        {renderTrigger}
      </Popover.Trigger>
      <Popover.Content
        // @note: custom popover content size
        className={cn("w-auto", popoverContentClassName)}
        spacing={false}
        size="fit"
        portalContainer={portalContainer}
      >
        <div className="flex justify-between p-2 space-x-1">
          <Select onValueChange={handleYearChange} value={year.toString()}>
            <Select.Trigger className="w-32" size="xs">
              <Select.Value
                placeholder={
                  getText("UI_Primitive_DatePicker_YearPlaceholder").Name
                }
              />
            </Select.Trigger>
            <Select.Content
              portalContainer={portalContainer}
              className={popoverContentClassName}
            >
              {years.map((y) => (
                <Select.Item key={y} value={y.toString()} className="py-1">
                  {y}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
          <Select onValueChange={handleMonthChange} value={month.toString()}>
            <Select.Trigger className="w-32" size="xs">
              <Select.Value
                placeholder={
                  getText("UI_Primitive_DatePicker_MonthPlaceholder").Name
                }
              />
            </Select.Trigger>
            <Select.Content
              portalContainer={portalContainer}
              className={popoverContentClassName}
            >
              {months.map((m, index) => (
                <Select.Item
                  key={index}
                  value={index.toString()}
                  className="py-1"
                >
                  {format(m, "MMMM", { locale: getDateFnsLocale() })}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        </div>
        <Calendar
          mode="single"
          // the month heading, weekday names and first weekday follow the UI
          // language, like the month select above
          locale={getDateFnsLocale()}
          disabled={
            disabled || [
              ...(minDate ? [{ before: minDate }] : []),
              ...(maxDate ? [{ after: maxDate }] : []),
            ]
          }
          selected={date}
          // a picked day is the answer, so the popover closes; the month and
          // year selects above keep it open while the user browses
          onSelect={(day) => {
            commitDate(day)
            setOpen(false)
          }}
          month={new Date(year, month)}
          onMonthChange={(newMonth) => {
            setMonth(newMonth.getMonth())
            setYear(newMonth.getFullYear())
          }}
          initialFocus
        />
        {showReset && (
          <div className="border-t">
            <Button
              variant="ghost"
              size="xs"
              className="w-full rounded-none rounded-b-md text-muted-foreground"
              disabled={!date}
              onClick={() => {
                setDate(undefined)
                setOpen(false)
              }}
            >
              {getText("UI_Primitive_DatePicker_Reset").Name}
            </Button>
          </div>
        )}
      </Popover.Content>
    </Popover>
  )
}
